import type { IntlShape } from 'react-intl'
import type { CurrentRefinementsConnectorParamsRefinement } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements'

import {
  getCaseItemStatusTranslationId,
  getCaseItemTypeTranslationId,
} from 'Utilities'

import type {
  CaseItemStatus,
  CaseItemType,
} from 'Portal/__generated__/globalTypes'
import { formatTimestampToDate } from 'Portal/src/utilities/dates/dates.utils'
import { getReturnCasePortalStatusTranslationId } from 'Portal/src/utilities/getReturnCasePortalStatus/getReturnCasePortalStatus.utils'

import {
  DATE_ATTRIBUTES,
  FILTER_SEPARATORS,
  RENTALS_DEVICE_CATEGORY_ATTRIBUTE,
  RENTALS_DEVICE_CATEGORY_CORE_VALUES,
} from './ActiveFilters.constants'

const isDate = (attribute: string) =>
  DATE_ATTRIBUTES.some(string => attribute.includes(string))

const isBoolean = (attribute: string) =>
  attribute.includes('is_') || attribute.includes('has_')

export const getSeparator = (
  refinements: CurrentRefinementsConnectorParamsRefinement[],
) => {
  const { attribute, type, value } = refinements[0]

  if (isDate(attribute)) {
    if (value === -1) {
      return FILTER_SEPARATORS.NONE
    }

    return FILTER_SEPARATORS.DATE
  }

  return type === 'numeric'
    ? FILTER_SEPARATORS.NUMERIC
    : FILTER_SEPARATORS.DEFAULT
}

export const parseRefinement = (
  refinement: CurrentRefinementsConnectorParamsRefinement,
  intl: IntlShape,
) => {
  const { attribute, label, value } = refinement

  if (attribute === 'case_item.status') {
    const translationId = getCaseItemStatusTranslationId(
      value as CaseItemStatus,
    )

    return intl.formatMessage({ id: translationId })
  }

  if (attribute === 'case_item.type') {
    const translationId = getCaseItemTypeTranslationId(value as CaseItemType)

    return intl.formatMessage({ id: translationId })
  }

  if (isDate(attribute)) {
    if (value === -1) {
      return null
    }

    return formatTimestampToDate(value)
  }

  if (isBoolean(attribute) && typeof value === 'number') {
    return value === 1
      ? intl.formatMessage({ id: 'Yes' })
      : intl.formatMessage({ id: 'No' })
  }

  return label
}

export const parseRentalsDeviceCategoryRefinements = (
  refinements: CurrentRefinementsConnectorParamsRefinement[],
  intl: IntlShape,
) => {
  const labels = new Set<string>()
  let hasOther = false

  for (const refinement of refinements) {
    if (refinement.attribute !== RENTALS_DEVICE_CATEGORY_ATTRIBUTE) {
      continue
    }

    const value = refinement.value.toString()

    if (RENTALS_DEVICE_CATEGORY_CORE_VALUES.has(value)) {
      labels.add(
        intl.formatMessage({
          id: `RentalsDeviceCategory_${value}`,
        }),
      )
    } else {
      hasOther = true
    }
  }

  if (hasOther) {
    labels.add(
      intl.formatMessage({
        id: 'RentalsDeviceCategory_other',
      }),
    )
  }

  return [...labels].join(', ')
}

export const parseReturnCasePortalStatusRefinements = (
  refinements: CurrentRefinementsConnectorParamsRefinement[],
  intl: IntlShape,
) =>
  refinements
    .reduce<string[]>((result, item) => {
      const { value } = item

      const formattedLabel = intl.formatMessage({
        id: getReturnCasePortalStatusTranslationId(value.toString()),
      })

      if (!result.includes(formattedLabel)) {
        result.push(formattedLabel)
      }

      return result
    }, [])
    .join(', ')
